import useDeclarations from 'hooks/useDeclarations';
import useGetDeclarationMapValues from 'hooks/useGetDeclarationMapValues';
import useFormUtils from 'hooks/useFormUtils';
import { box44PathAndFieldNames } from 'views/declarations/common/box44/mappers';
import { useMemo } from 'react';
import useSubmittedDeclaration from 'views/declarations/declaration-tabs/declaration-form/tabs/products/useSubmittedDeclaration';
import getErrorFieldContext from '../getErrorFieldContext';
import { ErrorCardContext } from './types';

/**
 * What the error cards need to name an error's item the way the products table does: the items as they
 * were submitted, the items today, and the declaration's parser for the field labels.
 *
 * `isLoaded` stays false until the history request has settled - until then an item could be named by
 * the wrong number. Without an item path there is no context, and the cards name no items.
 */
const useErrorCardContext = (): { context?: ErrorCardContext; isLoaded: boolean } => {
    const { declaration } = useDeclarations();
    const { itemPath, parseForm } = useGetDeclarationMapValues();
    const { formType } = useFormUtils();
    const { submittedDeclaration, isHistoryLoaded } = useSubmittedDeclaration();

    const context = useMemo(() => {
        const fieldContext = getErrorFieldContext(declaration, itemPath, submittedDeclaration);
        if (!fieldContext) return undefined;

        return {
            fieldContext,
            parser: parseForm,
            box44Paths: formType ? box44PathAndFieldNames[formType] : undefined,
        };
    }, [itemPath, parseForm, submittedDeclaration, declaration, formType]);

    return { context, isLoaded: isHistoryLoaded };
};

export default useErrorCardContext;
